var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../../sutl';
import { AdvancedHttpRequest } from './AdvancedHttpRequest';
import { HttpRequestEmulator } from './HttpRequestEmulator';
import { Timeouts } from './Timeouts';
import { TransportError } from './Transport';
const defaultTimeoutMs = 3000;
export const defaultHttpRequestOptions = {
    dataFormat: 'json',
    idleTimeoutMs: 20000,
    prettyCommands: false,
    scheme: 'https',
};
// let acceptAllCertsSet = false;
function formatTags(tags) {
    if (tags.length === 0)
        return '';
    return ` [${tags.join(', ')}]`;
}
export class HttpRequest {
    constructor(_host, logFactory, options, emulatorKey) {
        // bind callbacks
        this._host = _host;
        this._isClosed = false;
        this.isSending = false;
        this.sendQueue = [];
        this.timeouts = new Timeouts();
        this.onIdleTimeout = this.onIdleTimeout.bind(this);
        this.sendNextHttpRequest = this.sendNextHttpRequest.bind(this);
        // initialization
        this.driverLog = logFactory.channel('HttpRequest', 'driver');
        this.log = logFactory.channel('HttpRequest');
        this.idleTimeout = this.timeouts.create();
        this.options = Object.assign(Object.assign({}, defaultHttpRequestOptions), (options ? options : {}));
        if (this.options.idleTimeoutMs > 0) {
            this.idleTimeout.set(this.onIdleTimeout, this.options.idleTimeoutMs);
        }
        if (emulatorKey) {
            this._emulator = new HttpRequestEmulator(emulatorKey, logFactory);
            this.httpRequest = this._emulator;
        }
        else {
            this.httpRequest = AdvancedHttpRequest;
        }
    }
    get emulator() {
        var _a;
        return (_a = this._emulator) === null || _a === void 0 ? void 0 : _a.emulator;
    }
    get host() {
        return this._host;
    }
    get isClosed() {
        return this._isClosed;
    }
    close() {
        if (this._isClosed)
            this.log.error('already closed');
        this._isClosed = true;
        this.sendQueue.length = 0;
        this.timeouts.clearAll();
    }
    hasCommands(tag) {
        if (tag === undefined) {
            return this.sendQueue.length > 0;
        }
        else if (typeof tag === 'string') {
            for (const qr of this.sendQueue) {
                if (qr.tags.includes(tag))
                    return true;
            }
        }
        else {
            for (const tagElement of tag) {
                if (this.hasCommands(tagElement))
                    return true;
            }
        }
        return false;
    }
    onConnectionLost(connectionLostCallback) {
        this.clientConnectionLostCallback = connectionLostCallback;
        return this;
    }
    registerAsync(handler) {
        throw new Error('async handlers not supported in http drivers');
    }
    removeCommands(tag) {
        if (tag === undefined) {
            this.log.debug('removing all commands');
            const error = new TransportError('removing all commands', 'commandRemoved');
            for (const qr of this.sendQueue) {
                qr.reject(error);
            }
            this.sendQueue.length = 0;
        }
        else if (typeof tag === 'string') {
            Sutl.filterInPlace(this.sendQueue, qr => {
                if (qr.tags.includes(tag)) {
                    const message = `removing commands with tag: ${tag}`;
                    this.log.debug(message);
                    qr.reject(new TransportError(message, 'commandRemoved'));
                    return false;
                }
                return true;
            });
        }
        else {
            for (const tagElement of tag) {
                this.removeCommands(tagElement);
            }
        }
    }
    request(method, path, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._isClosed)
                throw new Error('request while closed');
            return new Promise((resolve, reject) => {
                var _a, _b, _c, _d;
                const req = {
                    isAsync: (_a = options.isAsync) !== null && _a !== void 0 ? _a : false,
                    req: {
                        host: this._host,
                        method,
                        path: path,
                        scheme: this.options.scheme,
                        serializer: (_b = options.serializer) !== null && _b !== void 0 ? _b : this.options.dataFormat,
                        timeout: ((_c = options.timeoutMs) !== null && _c !== void 0 ? _c : defaultTimeoutMs) * 0.001,
                    },
                    reject,
                    resolve,
                    tags: (_d = options.tags) !== null && _d !== void 0 ? _d : [],
                };
                if (options.data !== undefined)
                    req.req.data = options.data;
                if (options.headers !== undefined)
                    req.req.headers = options.headers;
                if (options.params !== undefined) {
                    req.req.params = {};
                    for (const key of Object.keys(options.params)) {
                        req.req.params[key] = options.params[key].toString();
                    }
                }
                if (this.isSending)
                    this.driverLog.debug(`queued: ${this.formatCommand(req)}`);
                this.sendQueue.push(req);
                this.timeouts.set(this.sendNextHttpRequest);
            });
        });
    }
    // TODO: do we need this method? just call request with isAsync set in options
    requestAsync(callback, method, path, options) {
        if (options === undefined)
            options = {};
        options.isAsync = true;
        this.request(method, path, options).then((response) => {
            callback(response);
        });
    }
    requestJson(method, path, options
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.request(method, path, options);
            if (resp.status !== 200) {
                const message = `commandFailed: ${this.formatCommand(resp)}`;
                throw new TransportError(message, 'commandFailed');
            }
            return JSON.parse((_a = resp.data) !== null && _a !== void 0 ? _a : '{}');
        });
    }
    unregisterAsync(id) {
        throw new Error('async handlers not supported in http drivers');
    }
    onIdleTimeout() {
        var _a;
        this.log.info('idle timeout, disconnecting');
        this.close();
        (_a = this.clientConnectionLostCallback) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    formatCommand(command) {
        if (this.options.prettyCommands) {
            const data = 'req' in command ? command.req.data : command.data;
            if (this.options.dataFormat === 'json' && typeof data === 'string') {
                const prettyCommand = Object.assign(Object.assign({}, command), { data: JSON.parse(data) });
                return JSON.stringify(prettyCommand, null, 4);
            }
            else {
                return JSON.stringify(command, null, 4);
            }
        }
        else {
            return JSON.stringify(command);
        }
    }
    // TODO: decouple this so we can reject the current in-progress request if we get a call to
    // remove all commands.
    sendNextHttpRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isSending)
                return;
            const req = this.sendQueue.shift();
            if (!req)
                return;
            this.isSending = true;
            this.driverLog.debug(`sending: ${this.formatCommand(req)}`);
            // Set acceptAllCerts(true) to work around SNTY-2113.
            // https://github.com/silkimen/cordova-plugin-advanced-http/issues/150
            //if (!acceptAllCertsSet) {
            //    await this.httpRequest.acceptAllCerts(true);
            //    acceptAllCertsSet = true;
            //}
            yield this.httpRequest.acceptAllCerts(true);
            if (req.isAsync) {
                // TODO: remove this condition if we remove requestAsync
                this.httpRequest.sendRequest(req.req).then((response) => {
                    if (!this._isClosed) {
                        if (this.options.idleTimeoutMs > 0) {
                            this.idleTimeout.set(this.onIdleTimeout, this.options.idleTimeoutMs);
                        }
                        const tags = formatTags(req.tags);
                        const msg = `received (async)${tags}: ${this.formatCommand(response)}`;
                        this.driverLog.debug(msg);
                        req.resolve(response);
                    }
                });
            }
            else {
                try {
                    const response = yield this.httpRequest.sendRequest(req.req);
                    if (!this._isClosed) {
                        if (this.options.idleTimeoutMs > 0) {
                            this.idleTimeout.set(this.onIdleTimeout, this.options.idleTimeoutMs);
                        }
                        const tags = formatTags(req.tags);
                        this.driverLog.debug(`received${tags}: ${this.formatCommand(response)}`);
                        req.resolve(response);
                    }
                }
                catch (error) {
                    req.reject(error);
                }
            }
            this.isSending = false;
            this.timeouts.set(this.sendNextHttpRequest);
        });
    }
}
